// In src/pages/Profile.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc, Timestamp, setDoc } from 'firebase/firestore';
import { uploadFile, deleteFileByUrl, resizeImage } from '../services/storageService';

import {
  TextField, Button, Container, Typography, Box, CircularProgress, Alert, Grid,
  FormControl, InputLabel, Select, MenuItem, Avatar, FormControlLabel, Switch,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';

const usStates = [
  { name: 'Alabama', abbr: 'AL' }, { name: 'Alaska', abbr: 'AK' }, { name: 'Arizona', abbr: 'AZ' },
  { name: 'Arkansas', abbr: 'AR' }, { name: 'California', abbr: 'CA' }, { name: 'Colorado', abbr: 'CO' },
  { name: 'Connecticut', abbr: 'CT' }, { name: 'Delaware', abbr: 'DE' }, { name: 'Florida', abbr: 'FL' },
  { name: 'Georgia', abbr: 'GA' }, { name: 'Hawaii', abbr: 'HI' }, { name: 'Idaho', abbr: 'ID' },
  { name: 'Illinois', abbr: 'IL' }, { name: 'Indiana', abbr: 'IN' }, { name: 'Iowa', abbr: 'IA' },
  { name: 'Kansas', abbr: 'KS' }, { name: 'Kentucky', abbr: 'KY' }, { name: 'Louisiana', abbr: 'LA' },
  { name: 'Maine', abbr: 'ME' }, { name: 'Maryland', abbr: 'MD' }, { name: 'Massachusetts', abbr: 'MA' },
  { name: 'Michigan', abbr: 'MI' }, { name: 'Minnesota', abbr: 'MN' }, { name: 'Mississippi', abbr: 'MS' },
  { name: 'Missouri', abbr: 'MO' }, { name: 'Montana', abbr: 'MT' }, { name: 'Nebraska', abbr: 'NE' },
  { name: 'Nevada', abbr: 'NV' }, { name: 'New Hampshire', abbr: 'NH' }, { name: 'New Jersey', abbr: 'NJ' },
  { name: 'New Mexico', abbr: 'NM' }, { name: 'New York', abbr: 'NY' }, { name: 'North Carolina', abbr: 'NC' },
  { name: 'North Dakota', 'abbr': 'ND' }, { name: 'Ohio', abbr: 'OH' }, { name: 'Oklahoma', abbr: 'OK' },
  { name: 'Oregon', abbr: 'OR' }, { name: 'Pennsylvania', abbr: 'PA' }, { name: 'Rhode Island', abbr: 'RI' },
  { name: 'South Carolina', abbr: 'SC' }, { name: 'South Dakota', 'abbr': 'SD' }, { name: 'Tennessee', abbr: 'TN' },
  { name: 'Texas', abbr: 'TX' }, { name: 'Utah', abbr: 'UT' }, { name: 'Vermont', abbr: 'VT' },
  { name: 'Virginia', abbr: 'VA' }, { name: 'Washington', abbr: 'WA' }, { name: 'West Virginia', abbr: 'WV' },
  { name: 'Wisconsin', abbr: 'WI' }, { name: 'Wyoming', abbr: 'WY' },
  { name: 'District of Columbia', abbr: 'DC' }, { name: 'American Samoa', abbr: 'AS' },
  { name: 'Guam', abbr: 'GU' }, { name: 'Northern Mariana Islands', abbr: 'MP' },
  { name: 'Puerto Rico', abbr: 'PR' }, { name: 'U.S. Virgin Islands', abbr: 'VI' }
];

function Profile() {
  const { currentUser, churchProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '',
    email: '',
    phone: '', address: '', city: '', state: '', zip: '', birthday: null,
  });
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [eventRemindersOptIn, setEventRemindersOptIn] = useState(false);
  const [generalNotificationsOptIn, setGeneralNotificationsOptIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchProfile = useCallback(async () => {
    if (currentUser) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const privateContactInfoDocRef = doc(db, 'users', currentUser.uid, 'privateContactInfo', 'details');

      const [userDocSnap, privateContactInfoDocSnap] = await Promise.all([
        getDoc(userDocRef),
        getDoc(privateContactInfoDocRef)
      ]);

      if (userDocSnap.exists()) {
        const profileData = userDocSnap.data();
        const privateData = privateContactInfoDocSnap.exists() ? privateContactInfoDocSnap.data() : {};

        setFormData({
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          email: privateData.email || currentUser.email || '',
          phone: privateData.phone || '',
          address: privateData.address || '',
          city: privateData.city || '',
          state: privateData.state || '',
          zip: privateData.zip || '',
          birthday: privateData.birthday ? privateData.birthday.toDate() : null,
        });
        setProfilePhotoUrl(profileData.profilePhotoUrl || '');
        setEmailOptIn(profileData.emailOptIn || false);
        setEventRemindersOptIn(profileData.notificationSettings?.eventRemindersOptIn || false);
        setGeneralNotificationsOptIn(profileData.generalNotificationsOptIn ?? true);
      } else {
        setError("Could not find your profile data.");
      }
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prevState => ({ ...prevState, birthday: newDate }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentUser?.uid) return;

    setUploadingPhoto(true);
    setError('');
    setSuccess('');

    try {
      if (profilePhotoUrl) {
        await deleteFileByUrl(profilePhotoUrl);
      }
      const resizedFile = await resizeImage(file, 200, 200, 0.8);
      const photoPath = `users/${currentUser.uid}/profilePhotos/${Date.now()}_${resizedFile.name}`;
      const newPhotoUrl = await uploadFile(resizedFile, photoPath);

      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { profilePhotoUrl: newPhotoUrl });
      setProfilePhotoUrl(newPhotoUrl);
      setSuccess('Profile photo updated successfully!');
    } catch (err) {
      console.error("Error uploading profile photo:", err);
      setError(`Failed to upload photo: ${err.message}`);
    } finally {
      setUploadingPhoto(false);
      e.target.value = null;
    }
  };

  const handlePhotoDelete = async () => {
    if (!profilePhotoUrl || !currentUser?.uid) return;
    if (!window.confirm("Are you sure you want to remove your profile photo?")) return;

    setUploadingPhoto(true);
    setError('');
    setSuccess('');

    try {
      await deleteFileByUrl(profilePhotoUrl);
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, { profilePhotoUrl: null });
      setProfilePhotoUrl('');
      setSuccess('Profile photo removed successfully!');
    } catch (err) {
      console.error("Error deleting profile photo:", err);
      setError(`Failed to remove photo: ${err.message}`);
    } finally {
      setUploadingPhoto(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setError("You are not logged in.");
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    const userDocRef = doc(db, 'users', currentUser.uid);
    const privateContactInfoDocRef = doc(db, 'users', currentUser.uid, 'privateContactInfo', 'details');

    try {
      const mainUserDataToUpdate = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        emailOptIn: emailOptIn,
        generalNotificationsOptIn: generalNotificationsOptIn,
        notificationSettings: {
          eventRemindersOptIn: eventRemindersOptIn,
        },
      };

      const privateContactDataToUpdate = {
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        birthday: formData.birthday ? Timestamp.fromDate(new Date(formData.birthday)) : null,
      };

      await setDoc(userDocRef, { ...mainUserDataToUpdate }, { merge: true });
      await setDoc(privateContactInfoDocRef, privateContactDataToUpdate, { merge: true });

      setSuccess('Profile updated successfully!');
    } catch (err) {
      console.error("Error updating document: ", err);
      setError(`Failed to update profile: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>My Profile</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={profilePhotoUrl || ''}
              alt={`${formData.firstName} ${formData.lastName}`}
              sx={{ width: 100, height: 100, mb: 2 }}
            >
              {!profilePhotoUrl && formData.firstName.charAt(0) + formData.lastName.charAt(0)}
            </Avatar>
            <Box>
              {!profilePhotoUrl ? (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <CircularProgress size={20} /> : 'Upload Photo'}
                  <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <CircularProgress size={20} /> : 'Change Photo'}
                  <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                </Button>
              )}
              {profilePhotoUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  sx={{ ml: 1 }}
                  onClick={handlePhotoDelete}
                  disabled={uploadingPhoto}
                >
                  Remove Photo
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom>
            Email: {formData.email} (cannot be changed via this form)
          </Typography>

          <TextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} fullWidth margin="normal" />
          <TextField label="Street Address" name="address" value={formData.address} onChange={handleChange} fullWidth margin="normal" />

          {/* CORRECTED: The props here are now compatible with MUI Grid v2 */}
          <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="City" name="city" value={formData.city} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select label="State" name="state" value={formData.state} onChange={handleChange}>
                  {usStates.map((s) => ( <MenuItem key={s.abbr} value={s.abbr}>{s.abbr}</MenuItem> ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth label="Zip Code" name="zip" value={formData.zip} onChange={handleChange} />
            </Grid>
          </Grid>

          <DatePicker
            label="Birthday"
            value={formData.birthday}
            onChange={handleDateChange}
            slots={{ textField: TextField }}
            slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
            enableAccessibleFieldDOMStructure={false}
          />
          
          <Box sx={{ mt: 2, mb: 1, borderTop: '1px solid #eee', pt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Notification Settings</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={generalNotificationsOptIn}
                  onChange={(e) => setGeneralNotificationsOptIn(e.target.checked)}
                  name="generalNotificationsOptIn"
                  disabled={!churchProfile?.enablePushNotifications}
                />
              }
              label="Receive all push notifications and alerts"
              sx={{ display: 'block' }}
            />
            {churchProfile?.enableEmailing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    name="emailOptIn"
                  />
                }
                label="Receive email notifications and newsletters"
                sx={{ display: 'block' }}
              />
            )}
            {churchProfile?.enableEventReminders && (
              <FormControlLabel
                control={
                  <Switch
                    checked={eventRemindersOptIn}
                    onChange={(e) => setEventRemindersOptIn(e.target.checked)}
                    name="eventRemindersOptIn"
                    disabled={!generalNotificationsOptIn}
                  />
                }
                label="Receive push notification reminders for events"
                sx={{ display: 'block' }}
              />
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              sx={{ ml: 2 }}
              onClick={() => navigate('/profile/volunteer-history')}
            >
              View My Volunteer History
            </Button>
          </Box>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}

export default Profile;